import * as tslib_1 from "tslib";
import addInfoBarMessage from './addInfoBarMessage';
import removeInfoBarMessage from './removeInfoBarMessage';
import datapoints from '../datapoints';
import { sendAndSaveInfobarIdsToRemove } from '../utils/InfoBarMessageId';
import { handleErrorResponse, handleSuccessResponse, saveMessage } from '../utils/sendSaveUtils';
import { getTotalRecipientCount } from '../utils/viewStateUtils';
import isEncryptionFeatureEnabled from '../utils/isEncryptionFeatureEnabled';
import triggerPolicyTips from '../actions/triggerPolicyTips';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { action } from 'satcheljs/lib/legacy';
import { logSmartReplyDatapoint } from '../utils/logSmartReplyComposeDatapoint';
var saveActionManagerMap = {};
function ensureActionManager(viewState) {
    var manager = saveActionManagerMap[viewState.composeId];
    if (!manager) {
        manager = {
            activeSaveAction: null,
            hasPendingSaveAction: false,
        };
        saveActionManagerMap[viewState.composeId] = manager;
    }
    return manager;
}
// When saving cost a longer time due to server side delay, it is possible that user triggered another saving or even more
// What we do here is:
// 1. If there's no active saving, return true to trigger a saving action
// 2. If there is active saving, we want to queue only one saving action. So check hasPendingSaveAction,
//    if it is true, it means another pending saving is queued, so return false here to avoid queue a 3rd saving.
//    if it is false, it means there is only one active saving and not pending saving queued, so set hasPendingSaveAction to true
//    to avoid another pending saving queued, and wait for current active saving then return true to save again
function waitForCurrentSavingAndCheckIfNeedToSaveAgain(viewState) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        var manager;
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    manager = ensureActionManager(viewState);
                    if (!!manager.activeSaveAction) return [3 /*break*/, 1];
                    // no active save, so we should save now
                    return [2 /*return*/, Promise.resolve(true)];
                case 1:
                    if (!manager.hasPendingSaveAction) return [3 /*break*/, 3];
                    // there is already another pending save action, just wait for active saving but no need to save again
                    return [4 /*yield*/, manager.activeSaveAction];
                case 2:
                    // there is already another pending save action, just wait for active saving but no need to save again
                    _a.sent();
                    return [2 /*return*/, Promise.resolve(false)];
                case 3:
                    // otherwise, need to save again after current saving finished, so wait it then return true
                    // set hasPendingSaveAction to true so if this is called again before the active saving is finished,
                    // the caller doesn't need to save the 3rd time
                    manager.hasPendingSaveAction = true;
                    return [4 /*yield*/, manager.activeSaveAction];
                case 4:
                    _a.sent();
                    manager.hasPendingSaveAction = false;
                    return [2 /*return*/, Promise.resolve(true)];
            }
        });
    });
}
// Save with a given saving action, wait until it finishes
// This will set activeSavingAction to saveActionManager so that other places will know a saving action is active
export function saveWithAction(viewState, saveAction) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        var manager;
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    manager = ensureActionManager(viewState);
                    manager.activeSaveAction = saveAction;
                    return [4 /*yield*/, saveAction
                            .then(function (result) {
                            manager.activeSaveAction = null;
                            return Promise.resolve(result);
                        })
                            .catch(function (error) {
                            manager.activeSaveAction = null;
                            return Promise.reject(error);
                        })];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    });
}
// Wait for active saving if any. Return true if there is active saving action, otherwise return false
export function waitForActiveSaving(viewState) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        var manager;
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    manager = saveActionManagerMap[viewState.composeId];
                    if (!(manager && manager.activeSaveAction)) return [3 /*break*/, 2];
                    return [4 /*yield*/, manager.activeSaveAction];
                case 1:
                    _a.sent();
                    return [2 /*return*/, Promise.resolve(true)];
                case 2: return [2 /*return*/, Promise.resolve(false)];
            }
        });
    });
}
export function isSaving(viewState) {
    var manager = saveActionManagerMap[viewState.composeId];
    return manager && !!manager.activeSaveAction;
}
export function cleanUpSaveActionManager(viewState) {
    if (saveActionManagerMap[viewState.composeId]) {
        delete saveActionManagerMap[viewState.composeId];
    }
}
function validateMaxRecipients(viewState) {
    var totalRecipients = getTotalRecipientCount(viewState);
    return totalRecipients <= getUserConfiguration().MaxRecipientsPerMessage;
}
export default action('trySaveMessage', datapoints.MailComposeSave)(function trySaveMessage(viewState, isAutoSave, rejectWhenFail) {
    if (isAutoSave === void 0) { isAutoSave = false; }
    if (rejectWhenFail === void 0) { rejectWhenFail = false; }
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!viewState) {
                        return [2 /*return*/, Promise.reject('could not save message if view state does not exist')];
                    }
                    if (viewState.isSending) {
                        // Already sending, so no need to save again
                        return [2 /*return*/, Promise.resolve()];
                    }
                    if (!isAutoSave) {
                        removeInfoBarMessage(viewState, sendAndSaveInfobarIdsToRemove);
                    }
                    if (!validateMaxRecipients(viewState)) {
                        if (!isAutoSave) {
                            addInfoBarMessage(viewState, 'warningMessageMaxRecipientsExceed');
                        }
                        return [2 /*return*/, Promise.resolve()];
                    }
                    return [4 /*yield*/, waitForCurrentSavingAndCheckIfNeedToSaveAgain(viewState)];
                case 1:
                    // Check if there is active saving and if need to save again
                    if (!(_a.sent())) {
                        return [2 /*return*/, Promise.resolve()];
                    }
                    return [2 /*return*/, saveWithAction(viewState, saveMessage(viewState)
                            .then(function (responseMessage) {
                            handleSuccessResponse(responseMessage, viewState);
                            if (isEncryptionFeatureEnabled()) {
                                triggerPolicyTips(viewState, isAutoSave ? 2 /* AutoSave */ : 4 /* Save */);
                            }
                            // If we are saving a message created via smart reply, log a datapoint.
                            logSmartReplyDatapoint(1 /* Save */, viewState.smartReplyExtractionId);
                            return Promise.resolve();
                        })
                            .catch(function (error) {
                            if (!isAutoSave) {
                                handleErrorResponse(error, viewState, false /*isSend*/);
                            }
                            return rejectWhenFail ? Promise.reject(error) : Promise.resolve();
                        }))];
            }
        });
    });
});
//# sourceMappingURL=trySaveMessage.js.map